import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import logoImg from "@/assets/logo.png";
import { Lock, Loader2 } from "lucide-react";

const BG = "https://images.pexels.com/photos/12534264/pexels-photo-12534264.jpeg";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Operator");
      navigate("/");
    } catch (e2) {
      setErr(apiErr(e2, "Échec de l'authentification"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#0A0A0A] text-white">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="terrain" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-chivo text-4xl font-black tracking-tighter uppercase leading-none">Tactical<br />Drone Ops</h2>
          <p className="font-mono text-xs text-[#A3A3A3] mt-4 tracking-wider max-w-md">
            Centre de contrôle de flotte connectée. Télémétrie temps réel, commandes de vol, planification de mission.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="group w-9 h-9 rounded-sm overflow-hidden flex items-center justify-center bg-black ring-1 ring-transparent transition-[box-shadow] duration-300 hover:ring-[#3B82F6]/60 hover:shadow-[0_0_16px_3px_rgba(59,130,246,0.55)]">
              <img src={logoImg} alt="SkyCore AI" className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110" data-testid="login-logo" />
            </div>
            <div>
              <div className="font-chivo font-black tracking-tighter text-lg uppercase leading-none">SkyCore AI</div>
              <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#525252]">Drone Gateway</div>
            </div>
          </div>

          <h1 className="font-chivo text-2xl font-bold tracking-tight uppercase mb-1">
            {mode === "login" ? "Connexion" : "Créer un compte"}
          </h1>
          <p className="font-mono text-xs text-[#A3A3A3] mb-6">Accès console opérateur</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Input label="Nom" value={name} onChange={setName} testid="register-name" placeholder="Operator" />
            )}
            <Input label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
            <Input label="Mot de passe" type="password" value={password} onChange={setPassword} testid="login-password" />

            {err && <div data-testid="login-error" className="font-mono text-xs text-[#EF4444] border border-[#EF4444]/30 bg-[#EF4444]/10 rounded-sm px-3 py-2">{err}</div>}

            <button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-50 text-white rounded-sm font-mono tracking-widest uppercase text-xs py-3 flex items-center justify-center gap-2 transition-colors">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              {mode === "login" ? "Se connecter" : "S'inscrire"}
            </button>
          </form>

          <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            className="mt-5 font-mono text-xs text-[#A3A3A3] hover:text-white transition-colors">
            {mode === "login" ? "Pas de compte ? Créer un compte" : "Déjà inscrit ? Se connecter"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] block mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#141414] border border-white/15 focus:border-[#3B82F6] outline-none rounded-sm px-3 py-2.5 font-mono text-sm transition-colors" />
    </div>
  );
}
